import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter, type Event } from '@patsnap/tahiti-core/common/event';
import { Message } from '@patsnap/tahiti-core/common/message';
import { Symbols } from '@/constants';
import { AISearchDescriptionModel } from '../model/ai-search-description.model';
import { AISearchRepository } from './ai-search.repository.interface';
import type { DescriptionState } from '../type/ai-search.type';
import type VueI18n from 'vue-i18n';

export const AISearchDescriptionService = Symbol('AISearchDescriptionService');

export interface AISearchDescriptionService {
    getModelData: () => DescriptionState;
    readonly onStatesChange: Event<DescriptionState>;
    readonly onSummaryGet: Event<DescriptionState['desc']>;

    updateDescription: (desc: string) => void;
    handleClearDescription: () => void;
    handleGetSummary: () => void;
}

@injectable()
export class AISearchDescriptionServiceImpl implements AISearchDescriptionService {
    private _onStatesChangeEmitter = new Emitter<DescriptionState>();
    readonly onStatesChange = this._onStatesChangeEmitter.event;

    private _onSummaryGetEmitter = new Emitter<DescriptionState['desc']>();
    readonly onSummaryGet = this._onSummaryGetEmitter.event;
    
    constructor(
        @inject(Message)
        private _message: Message,
        @inject(Symbols.VueI18n)
        private _i18n: VueI18n,
        @inject(AISearchDescriptionModel)
        private _model: AISearchDescriptionModel,
        @inject(AISearchRepository)
        private _repository: AISearchRepository,
    ){
        this._model.onStatesChange(this._emitModelDataChange.bind(this));
    }

    private _emitModelDataChange() {
        this._onStatesChangeEmitter.fire(this.getModelData());
    }

    public getModelData(): DescriptionState {
        return this._model.modelData;
    }

    private _setModelData(value: Partial<DescriptionState>) {
        this._model.update(modelData => {
            return { ...modelData, ...value };
        });
    }

    public updateDescription(desc: string) {
        this._setModelData({ desc });
    }

    private async _checkPromptLawful() {
        this._setModelData({ checkLawfulLoading: true });
        const lawful = await this._repository.checkSensitiveWord(this.getModelData().desc);
        this._setModelData({ checkLawfulLoading: false });
        if (!lawful) {
            this._message.closeAll();
            this._message.warning({
                message: this._i18n.t('aiSearch.sensitiveError') as string,
                showClose: true,
            });
        }
        return lawful;
    }

    public handleClearDescription() {
        this._setModelData({ desc: '' });
    }

    public async handleGetSummary() {
        const lawful = await this._checkPromptLawful();
        if (!lawful) {
            return;
        }
        this._onSummaryGetEmitter.fire(this.getModelData().desc);
    }
}